import { useState } from "react";
import { motion } from "framer-motion";
import { Calendar, ArrowRight, Play, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import CalendlySection from "@/components/landing/CalendlySection";
import WhatsAppButton from "@/components/landing/WhatsAppButton";
import videoCover from "@/assets/video-cover-capital.jpg";

const sections = [
  {
    title: "¿Con qué querés que asocien tu nombre?",
    body: `La autoridad no empieza publicando.

Empieza definiendo una posición.

Hay empresarios que hablan de liderazgo, ventas, innovación, management, negocios, tendencias, familia, actualidad y cualquier tema que aparezca.

Están presentes.

Pero resulta difícil explicar por qué deberíamos escucharlos a ellos.

Un posicionamiento fuerte permite completar esta frase:

“[Nombre] es una de las personas que entiende de ________.”

Ese espacio en blanco es estratégico.

No necesariamente tiene que ser el nombre de tu industria.

Puede ser un problema que conocés profundamente, una transformación que sabés dirigir o una mirada particular que desarrollaste después de años tomando decisiones.`,
    highlight: `No busques hablar de muchas cosas.
Buscá ser asociado con algo valioso.`,
  },
  {
    title: "Identificá el conocimiento que para vos ya parece obvio",
    body: `Después de muchos años en una industria ocurre algo curioso:

dejás de percibir como extraordinario lo que sabés.

Sabés reconocer un problema antes de que aparezca.

Detectás rápidamente cuándo una decisión puede salir cara.

Entendés comportamientos del mercado que alguien nuevo todavía no ve.

Tenés criterios para elegir proveedores, contratar personas, negociar, invertir o decir que no.

Y muchas veces respondés:

“Eso es sentido común.”

Probablemente no lo sea.

Es criterio construido durante años.

Ahí suele existir uno de los mayores activos para construir autoridad.`,
    highlight: `La pregunta no es solamente: ¿Qué sabés?

Es: ¿Qué podés ver hoy que hace 15 años todavía no podías ver?

Ahí empieza a aparecer tu verdadero diferencial.`,
  },
  {
    title: "Convertí experiencia en una mirada propia",
    body: `Saber mucho no alcanza para construir una posición.

El mercado tiene que poder reconocer cómo pensás.

Dos empresarios pueden tener 25 años en la misma industria y haber desarrollado conclusiones completamente diferentes.

Uno puede creer que el futuro está en la escala. Otro, en la especialización.

Uno puede competir desde eficiencia. Otro, desde experiencia.

Uno puede pensar que determinada práctica de su sector quedó obsoleta. Otro puede haber desarrollado una manera diferente de resolver el mismo problema.

Eso es valioso.

Porque la autoridad empieza a crecer cuando dejás de repetir información que cualquiera puede encontrar y empezás a instalar criterio propio.`,
    highlight: `No solamente: “5 tendencias de nuestra industria.”

Sino: “Después de 20 años trabajando en esta industria, hay una práctica que creo que deberíamos dejar de hacer.”

Eso tiene posición.`,
  },
  {
    title: "Hacé visible la evidencia",
    body: `La autoridad no se declara.

Se demuestra.

No alcanza con decir: “Tenemos mucha experiencia.” “Somos referentes.” “Somos líderes.” “Trabajamos con excelencia.”

El mercado necesita señales que le permitan llegar solo a esa conclusión.`,
    evidence: [
      { label: "Trayectoria", desc: "Los años que llevás tomando decisiones dentro del sector." },
      { label: "Casos", desc: "Problemas complejos que ayudaste a resolver." },
      { label: "Clientes", desc: "Empresas o personas que confiaron en vos." },
      { label: "Resultados", desc: "Transformaciones concretas que generaste." },
      { label: "Método", desc: "Una manera propia de hacer lo que hacés." },
      { label: "Criterio", desc: "Opiniones que nacen de experiencia real." },
      { label: "Ecosistema", desc: "Personas, instituciones y organizaciones con las que construiste relaciones." },
    ],
    highlight: `No se trata de mostrar todo.

Se trata de elegir qué evidencia eleva la percepción que querés construir.`,
  },
  {
    title: "Pasá de “publicar contenido” a construir autoridad",
    body: `Este cambio es importante.

El objetivo no debería ser: “Tenemos que publicar más.”

Debería ser: “Tenemos que conseguir que determinadas ideas sean asociadas con nuestro nombre.”

El contenido es solamente uno de los vehículos.

También construyen autoridad: una conferencia, una entrevista, un artículo, una columna, una conversación privada, una investigación, un caso, un evento, una alianza estratégica, una aparición en medios, una participación institucional, un libro, un método propio.`,
    highlight: `Por eso un empresario no debería preguntarse: “¿Qué publico esta semana?”

Sino: “¿Qué posición estamos construyendo durante los próximos años?”`,
  },
  {
    title: "Hacé que tu nombre eleve a tu empresa — y viceversa",
    body: `Cuando está bien construido, el posicionamiento del founder no compite con la marca corporativa.

La potencia.

La empresa aporta: trayectoria, estructura, resultados, clientes y evidencia.

El empresario aporta: visión, criterio, rostro, relaciones y autoridad.

Uno puede elevar al otro.

Y con el tiempo aparece algo todavía más interesante: el nombre del empresario empieza a funcionar como un activo independiente.

Puede abrir conversaciones. Atraer talento. Generar alianzas. Facilitar negocios. Dar confianza frente a nuevos mercados. Crear oportunidades que antes no llegaban a la empresa.`,
    highlight: `La marca personal deja entonces de ser una cuestión de visibilidad.

Se convierte en Capital de Marca.`,
  },
];

const testQuestions = [
  "Si alguien importante de tu industria escucha tu nombre, ¿sabe quién sos?",
  "Si sabe quién sos, ¿puede explicar qué representás?",
  "¿Existe alguna idea, criterio o especialidad que el mercado asocie particularmente con vos?",
  "Cuando alguien investiga tu nombre, ¿lo que encuentra refleja realmente la trayectoria que construiste?",
  "¿Tu posicionamiento personal genera oportunidades concretas para vos o para tu empresa?",
];

const videoQuestions = [
  "¿Quiénes son las 10 personas que sería estratégico que conocieran tu nombre?",
  "¿En qué espacios deberías empezar a participar para relacionarte con personas de ese nivel?",
  "¿Qué podrías aportar vos para convertirte en un contacto valioso para ellas?",
];

const GuiaPage = () => {
  const [videoStarted, setVideoStarted] = useState(false);

  const scrollToCalendly = () => {
    const el = document.getElementById("calendly");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <main className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-28 bg-[#470628] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#470628] via-[#600832] to-[#470628]" />
        <div className="container px-4 relative z-10 text-center max-w-4xl mx-auto">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-block text-[#FF9FBD] text-xs sm:text-sm font-semibold tracking-[0.25em] uppercase mb-6"
          >
            ELEVÁ TU CAPTIAL DE MARCA PERSONAL
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
          >
            Cómo transformar tu trayectoria en una{" "}
            <span className="text-[#FF9FBD]">posición de autoridad</span> dentro de tu industria
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-white/70 text-base md:text-lg max-w-2xl mx-auto"
          >
            Una guía para founders, dueños y empresarios que construyeron experiencia antes que audiencia.
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="text-white/50 text-xs md:text-sm tracking-[0.2em] uppercase mt-10"
          >
            CORESSEN®
          </motion.p>
        </div>
      </section>

      {/* Intro — No tenés que convertirte en influencer */}
      <section id="intro" className="py-16 md:py-24 bg-background">
        <div className="container px-4 max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-foreground leading-tight mb-8">
              No tenés que convertirte en <span className="text-primary">influencer</span>
            </h2>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              Muchos empresarios construyeron durante 15, 20 o 30 años algo que hoy tiene muchísimo valor: experiencia, relaciones, criterio, conocimiento del mercado, decisiones difíciles, errores, resultados y una manera propia de entender su industria.
            </p>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed mt-6">
              Sin embargo, cuando salen de su círculo cercano, prácticamente nadie lo sabe.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-gradient-to-b from-[#470628] to-[#600832] rounded-2xl p-8 md:p-12 text-white text-center shadow-xl"
          >
            <p className="text-white/90 text-lg md:text-xl leading-relaxed font-medium">
              La empresa tiene trayectoria. El empresario también.
            </p>
            <p className="font-display text-xl md:text-2xl font-bold mt-4 text-[#FF9FBD]">
              Pero esa trayectoria todavía no se convirtió en Capital de Marca.
            </p>
            <div className="w-16 h-px bg-white/30 mx-auto my-8" />
            <p className="text-white/80 leading-relaxed">
              Posicionarte como experto no significa empezar a mostrar tu vida, perseguir seguidores o convertirte en creador de contenido.
            </p>
            <p className="text-white/90 mt-4 font-medium leading-relaxed">
              Significa conseguir algo mucho más estratégico: que cuando el mercado piense en determinado problema, categoría o forma de hacer las cosas, pueda asociarlo con tu nombre.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Video — Círculo de Influencia */}
      <section id="video" className="py-16 md:py-24 bg-[#470628] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#470628] to-[#600832]" />
        <div className="container px-4 relative z-10 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <span className="inline-flex items-center gap-2 text-[#FF9FBD] text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase mb-4">
              <Play className="w-4 h-4" />
              Mirá
            </span>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-tight">
              Cómo construir tu círculo de influencia
            </h2>
            <p className="text-white/70 text-base md:text-lg mt-4 max-w-2xl mx-auto leading-relaxed">
              Tu próximo nivel de posicionamiento probablemente también dependa de las conversaciones a las que hoy todavía no tenés acceso.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10"
          >
            <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
              {videoStarted ? (
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube-nocookie.com/embed/0yLH4F6Hc6o?rel=0&modestbranding=1&controls=1&autoplay=1"
                  title="Cómo construir tu círculo de influencia"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <button
                  onClick={() => setVideoStarted(true)}
                  className="absolute inset-0 w-full h-full group cursor-pointer"
                  aria-label="Reproducir video"
                >
                  <img
                    src={videoCover}
                    alt="Portada del video: Cómo construir tu círculo de influencia"
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                    width={1280}
                    height={720}
                  />
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#FF9FBD] flex items-center justify-center shadow-lg shadow-black/30 group-hover:scale-110 transition-transform">
                      <Play className="w-6 h-6 sm:w-8 sm:h-8 text-[#470628] fill-[#470628] ml-1" />
                    </div>
                  </div>
                </button>
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-12"
          >
            <p className="text-white/90 font-display text-lg md:text-xl font-semibold text-center mb-8">
              Mientras lo mirás, pensá en estas tres preguntas:
            </p>
            <div className="grid gap-4 md:grid-cols-3">
              {videoQuestions.map((q, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#FF9FBD]/20 text-[#FF9FBD] flex items-center justify-center font-bold text-sm mb-4">
                    {i + 1}
                  </span>
                  <p className="text-white/85 text-sm md:text-base leading-relaxed">{q}</p>
                </div>
              ))}
            </div>
            <p className="text-white/60 text-center mt-8 leading-relaxed max-w-2xl mx-auto">
              No se trata de acercarte para venderles. Se trata de construir suficiente valor para que, cuando alguien guarde tu contacto, sepa exactamente por qué vale la pena tenerte cerca.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Secciones numeradas */}
      <section id="guia" className="py-16 md:py-24 bg-muted/30">
        <div className="container px-4 max-w-3xl mx-auto">
          {sections.map((section, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: 0.05 }}
              className="mb-20 last:mb-0"
            >
              <div className="flex items-start gap-4 sm:gap-5">
                <span className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-sm sm:text-base">
                  {i + 1}
                </span>
                <div className="flex-1">
                  <h2 className="font-display text-xl sm:text-2xl md:text-3xl font-bold text-foreground mb-6 leading-snug">
                    {section.title.toUpperCase()}
                  </h2>
                  <div className="text-muted-foreground leading-relaxed text-sm sm:text-base whitespace-pre-line">
                    {section.body}
                  </div>

                  {section.evidence && (
                    <div className="mt-8">
                      <p className="text-foreground font-display font-semibold text-base md:text-lg mb-4">
                        Algunas de las más poderosas ya pueden existir dentro de tu empresa:
                      </p>
                      <div className="grid gap-3 sm:grid-cols-2">
                        {section.evidence.map((item, j) => (
                          <div key={j} className="bg-background border border-border rounded-lg p-4">
                            <p className="font-semibold text-foreground text-sm md:text-base">{item.label}.</p>
                            <p className="text-muted-foreground text-sm mt-1">{item.desc}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="mt-8 border-l-4 border-primary bg-background rounded-r-xl p-6"
                  >
                    <Quote className="w-5 h-5 text-primary mb-3" />
                    <p className="font-display text-foreground font-semibold text-base md:text-lg leading-relaxed whitespace-pre-line">
                      {section.highlight}
                    </p>
                  </motion.div>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* El Test de Capital */}
      <section id="test" className="py-16 md:py-24 bg-[#470628] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#470628] to-[#600832]" />
        <div className="container px-4 relative z-10 max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
              El Test de Capital
            </h2>
            <p className="text-white/70 text-base md:text-lg">
              Cinco preguntas para detectar cuánto de tu trayectoria ya se convirtió en posicionamiento:
            </p>
          </motion.div>

          <div className="grid gap-4">
            {testQuestions.map((question, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-white/5 border border-white/10 rounded-xl p-6"
              >
                <div className="flex items-start gap-4">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#FF9FBD]/20 text-[#FF9FBD] flex items-center justify-center font-bold text-sm">
                    {i + 1}
                  </span>
                  <p className="font-display text-lg md:text-xl font-semibold text-white leading-snug">
                    {question}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-center mt-12"
          >
            <p className="text-white/80 text-base md:text-lg leading-relaxed">
              Si varias respuestas son no, probablemente no tengas un problema de experiencia.
            </p>
            <p className="font-display text-xl md:text-2xl font-bold text-white mt-4">
              Tenés una brecha entre el valor que construiste y el valor que el mercado está pudiendo percibir.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tu trayectoria ya existe */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container px-4 max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-6">
              Tu trayectoria ya existe
            </h2>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              No empezás de cero. Ese es uno de los errores más frecuentes de empresarios que empiezan a trabajar su posicionamiento después de muchos años. Creen que tienen que “crear una marca personal”.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-muted/40 border border-border rounded-2xl p-8 md:p-10"
          >
            <p className="text-foreground font-display font-semibold text-base md:text-lg mb-6 text-center">
              Pero ya existe materia prima:
            </p>
            <div className="flex flex-wrap justify-center gap-2 md:gap-3">
              {["los años", "las decisiones", "los clientes", "los errores", "los aprendizajes", "las relaciones", "los resultados", "las ideas", "la manera de dirigir", "la reputación que construiste"].map((item) => (
                <span key={item} className="px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
                  {item}
                </span>
              ))}
            </div>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed text-center mt-8">
              El trabajo no consiste en inventarte un personaje. Consiste en identificar ese capital, ordenarlo y convertirlo en una posición reconocible.
            </p>
            <p className="font-display text-foreground font-bold text-lg md:text-xl text-center mt-6 leading-snug">
              Porque después de años construyendo una empresa, quizás el próximo activo que vale la pena elevar sea tu propio nombre.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#470628] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#470628] to-[#600832]" />
        <div className="container px-4 relative z-10 text-center max-w-2xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6 leading-tight"
          >
            ¿Hay una brecha entre tu trayectoria y tu posicionamiento?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-white/80 text-base sm:text-lg leading-relaxed mb-10"
          >
            En CORESSEN trabajamos con empresarios y empresas con trayectoria para transformar experiencia, reputación y conocimiento en Capital de Marca. Si al leer esta guía sentiste que construiste mucho más de lo que hoy el mercado percibe, podemos analizar dónde está esa brecha.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Button
              variant="gold"
              size="xl"
              onClick={scrollToCalendly}
              className="text-base sm:text-lg px-8 sm:px-12"
            >
              <Calendar className="w-5 h-5 mr-2" />
              AGENDAR UNA LLAMADA DE CLARIDAD
            </Button>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-[#FF9FBD] font-display font-semibold text-sm md:text-base mt-10 flex items-center justify-center gap-2"
          >
            CORESSEN® <ArrowRight className="w-4 h-4" /> Elevamos la percepción a la altura del valor que construiste.
          </motion.p>
        </div>
      </section>

      <CalendlySection />
      <Footer />
      <WhatsAppButton />
    </main>
  );
};

export default GuiaPage;
